import moment from 'moment-timezone'

// Where a chart's time window starts when nothing else sets one.
export const DEFAULT_TIME_RANGE_FROM = 'now-30d'

// "Everything": the open start for datasets whose time filter rides an
// updated_at column, where the 30-day default hides most of the catalog.
export const OPEN_TIME_RANGE_FROM = '2000-01-01'

// The timestamp shape the backend compares against its local-time columns.
const TIMESTAMP_FORMAT = 'YYYY-MM-DD HH:mm:ss'

// Streamlit resolves `now` on a UTC clock and compares the result, as a naive
// string, with columns stored in local time (Laravel runs on Etc/GMT+6).
// Measured on both staging hosts on 2026-10-08: `from=now-10m` asks for rows
// stamped after UTC now minus 10 minutes, six hours ahead of the data, so it
// always returns nothing. See toStreamlitRelative.
export const STREAMLIT_NOW_UTC_OFFSET_MINUTES = 0

// Maps the short unit letters used in now-* expressions to moment.js unit strings.
const UNIT_MAP = {
  m: 'minutes',
  h: 'hours',
  d: 'days',
  w: 'weeks',
  M: 'months',
  y: 'years',
}

export const RELATIVE_UNITS = [
  { value: 'm', label: 'minutos' },
  { value: 'h', label: 'horas' },
  { value: 'd', label: 'días' },
]

const RELATIVE_PATTERN = /^(?:now|n)-(\d+)([mhdwMy])$/
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const DATE_MINUTES_PATTERN = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}$/

/**
 * Parse a `now-<n><unit>` expression.
 *
 * @param {string} value
 * @returns {{ amount: number, unit: string } | null}
 */
export function parseRelativeTime(value) {
  const match = String(value || '').trim().match(RELATIVE_PATTERN)
  if (!match) return null
  const amount = parseInt(match[1], 10)
  if (!amount || !UNIT_MAP[match[2]]) return null
  return { amount, unit: match[2] }
}

export function isRelativeTime(value) {
  return parseRelativeTime(value) !== null
}

export function buildRelativeTime(amount, unit) {
  const n = parseInt(amount, 10)
  if (!n || n < 1 || !UNIT_MAP[unit]) return ''
  return `now-${n}${unit}`
}

/**
 * Convert a relative time value (now-30d, now-1h, now) to a fixed local timestamp.
 * Outputs 'YYYY-MM-DD HH:mm:ss' so the backend treats it as local time,
 * avoiding UTC-offset mismatches.
 *
 * If the value is already absolute (no 'now'), it is returned unchanged.
 *
 * @param {string} value - e.g. 'now-30d', 'now-6h', 'now', or '2024-01-01T00:00:00Z'
 * @param {moment.Moment} [now] - the instant the expression is anchored to
 * @returns {string}
 */
export function resolveRelativeTime(value, now = moment()) {
  if (!value || !value.includes('now')) return value

  if (/^(now|n)$/i.test(value.trim())) {
    return now.clone().format(TIMESTAMP_FORMAT)
  }

  const parsed = parseRelativeTime(value)
  if (!parsed) return value

  return now.clone().subtract(parsed.amount, UNIT_MAP[parsed.unit]).format(TIMESTAMP_FORMAT)
}

/**
 * Normalize one bound of a range into the exact timestamp the backend compares.
 * A bare date or a time without seconds is widened to cover what the user
 * picked: the start of the day / minute for `from`, its last second for `to`
 * (the backend reads `to=2026-10-08` as midnight, dropping the whole day).
 * `now` as an end bound is kept, so live charts keep their open end.
 *
 * @param {string} value
 * @param {{ end?: boolean }} [options]
 * @returns {string}
 */
export function resolveTimeBound(value, { end = false } = {}) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  if (end && /^(now|n)$/i.test(raw)) return 'now'
  if (raw.includes('now')) return resolveRelativeTime(raw)
  if (DATE_ONLY_PATTERN.test(raw)) return `${raw} ${end ? '23:59:59' : '00:00:00'}`
  if (DATE_MINUTES_PATTERN.test(raw)) return `${raw.replace('T', ' ')}:${end ? '59' : '00'}`
  return raw
}

/**
 * Translate a relative `from` into the expression Streamlit must receive so the
 * window it computes lands on local time, letting its own live refresh keep the
 * window rolling. The window is measured in minutes and shifted by the gap
 * between the browser clock and the server clock; "last 10 minutes" in
 * UTC-6 travels as `now-370m`.
 *
 * Returns an absolute local timestamp when the shift cannot be expressed
 * (Streamlit rejects `now+…`, so a browser east of the server falls back).
 *
 * @param {string} value
 * @param {moment.Moment} [now]
 * @returns {string}
 */
export function toStreamlitRelative(value, now = moment()) {
  const parsed = parseRelativeTime(value)
  if (!parsed) return resolveRelativeTime(value, now)

  const start = now.clone().subtract(parsed.amount, UNIT_MAP[parsed.unit])
  const windowMinutes = Math.round(now.diff(start, 'minutes', true))
  const shift = STREAMLIT_NOW_UTC_OFFSET_MINUTES - now.utcOffset()
  const total = windowMinutes + shift

  if (total < 1) return resolveRelativeTime(value, now)
  return `now-${total}m`
}

/**
 * Resolve a time value exactly as Streamlit would, i.e. with its `now` on the
 * server clock. This is the inverse of toStreamlitRelative and the right way to
 * freeze a value read back from a chart URL.
 *
 * @param {string} value
 * @param {moment.Moment} [now]
 * @returns {string}
 */
export function resolveStreamlitTime(value, now = moment()) {
  if (!value || !value.includes('now')) return value
  const serverNow = now.clone().utcOffset(STREAMLIT_NOW_UTC_OFFSET_MINUTES)
  if (/^(now|n)$/i.test(value.trim())) return serverNow.format(TIMESTAMP_FORMAT)
  const parsed = parseRelativeTime(value)
  if (!parsed) return value
  return serverNow.subtract(parsed.amount, UNIT_MAP[parsed.unit]).format(TIMESTAMP_FORMAT)
}

/**
 * Freeze the from/to of a chart URL into fixed timestamps for CSV downloads and
 * PDF reports. The values come from the URL, so they carry Streamlit semantics.
 *
 * @param {string} from - start time (relative or absolute)
 * @param {string} to   - end time (relative or absolute)
 * @returns {{ from: string, to: string }}
 */
export function freezeTimeRange(from, to) {
  const now = moment()
  return {
    from: resolveStreamlitTime(from, now),
    to: to && !/^(now|n)$/i.test(to.trim())
      ? resolveStreamlitTime(to, now)
      : now.format(TIMESTAMP_FORMAT),
  }
}

/**
 * Whether an explicit range ends before it starts.
 *
 * @param {string} from
 * @param {string} to
 * @returns {boolean}
 */
export function isInvertedRange(from, to) {
  if (!from || !to) return false
  const start = moment(resolveTimeBound(from), TIMESTAMP_FORMAT, true)
  const end = moment(resolveTimeBound(to, { end: true }), TIMESTAMP_FORMAT, true)
  return start.isValid() && end.isValid() && end.isBefore(start)
}
